import React, { useState } from "react";
import { Form, Space, Radio, RadioChangeEvent, Tooltip } from "antd";
import {
  StepContainer,
  Title,
  DimensionsContainer,
  MeasurementUnitGroup,
  UnitLabel,
  DimensionGroup,
  DimensionLabel,
  DimensionInputGroup,
  UnitSelect,
  Note,
  WarningNote,
  ContentTextArea,
  InputWithUnit,
  DimensionGrid,
  NoteUl,
  NoteLi,
} from "./styles";
import { FormInstance } from "antd";
import StyledNumberInput from "../../../../components/StyledNumberInput";
import { InfoCircleOutlined } from '@ant-design/icons';
import { convertDimensionsUnits, getErrorMessage } from "../../utils/formula";


// Constants for unit divisors (used for unit labels only)
const IMPERIAL_DIVISORS = {
  length: 12, // 12 inches = 1 foot
  width: 12,
  height: 12,
  weight: 16, // 16 ounces = 1 pound
};

const METRIC_DIVISORS = {
  length: 100, // 100 cm = 1 meter
  width: 100,
  height: 100,
  weight: 1000, // 1000 grams = 1 kilogram
};

interface DimensionValue {
  main: number | null;
  sub: number | null;
}

type UnitType = 1 | 2; // 1 for Imperial, 2 for Metric

const Step3: React.FC<{ form: FormInstance }> = ({ form }) => {
  const unit: UnitType = Form.useWatch("measurementType", form) || 1;

  // No max values needed anymore

  // Get divisor based on unit type and dimension type
  const getDivisor = (type: "length" | "width" | "height" | "weight") => {
    return unit === 1 ? IMPERIAL_DIVISORS[type] : METRIC_DIVISORS[type];
  };

  // Get unit labels based on measurement type
  const getUnitLabels = (type: "length" | "width" | "height" | "weight") => {
    if (unit === 1) {
      return type === "weight" ? ["lb", ""] : ["", "in"];
    }
    return type === "weight" ? ["kg", ""] : ["", "cm"];
  };

  const handleUnitChange = async (e: RadioChangeEvent) => {
    const newUnit = e.target.value as UnitType;
    const oldUnit = unit;

    // Only perform conversion if there are values to convert
    const dimensionTypes: Array<"length" | "width" | "height" | "weight"> = ["length", "width", "height", "weight"];
    const hasValues = dimensionTypes.some(type => {
      const main = form.getFieldValue([type, "main"]);
      const sub = form.getFieldValue([type, "sub"]);
      return (main !== undefined && main !== null && main !== 0) || (sub !== undefined && sub !== null && sub !== 0);
    });
    if (hasValues && oldUnit !== newUnit) {
      // Convert existing values from old unit to new unit
      const values = await form.getFieldsValue();
      const convertedValues = convertDimensionsUnits(values, unit, newUnit);

      form.setFieldsValue({
        ...convertedValues,
        measurementType: newUnit,
      });
    } else {
      // If no values to convert, just reset and set the new unit type
      form.resetFields();
      form.setFieldValue("measurementType", newUnit);
    }
  };

  const validateNumber = (value: string | number | null): number | null => {
    if (value === null || value === "") return null;
    const num = typeof value === "string" ? parseFloat(value) : value;
    return isNaN(num) ? null : num;
  };

  const handleValueChange = (value: string | number | null, type: "length" | "width" | "height" | "weight", field: "main" | "sub") => {
    const currentValues = {
      main: form.getFieldValue([type, "main"]) || 0,
      sub: form.getFieldValue([type, "sub"]) || 0,
    };

    let newValues = { ...currentValues };
    if (field === "main") {
      const numValue = validateNumber(value);
      newValues.main = numValue !== null ? numValue : 0;
      form.setFieldsValue({
        [type]: newValues,
      });
    } else if (field === "sub") {
      const numValue = validateNumber(value);
      if (numValue !== null) {
        newValues.sub = numValue;
        form.setFieldsValue({
          [type]: newValues,
        });
      }
    }
  };

  const handleSubValueBlur = (type: "length" | "width" | "height" | "weight") => {
    // No conversion logic needed anymore
  };

  const renderDimensionInput = (type: "length" | "width" | "height" | "weight") => {
    const [mainUnit, subUnit] = getUnitLabels(type);
    if (!unit) return null;

    const getMainMaxValue = () => {
      // For weight: lb or kg (both 2 digits)
      if (type === "weight") return 99.99;
      // For dimensions: empty string means it's the sub-unit (cm/in)
      return 0;
    };

    const getSubMaxValue = () => {
      if (type === "weight") return 0; // No sub unit for weight
      return unit === 1 ? 99.99 : 999.99; // in: 2 digits (0-99), cm: 3 digits (0-999)
    };

    const validateMainValue = (value: number | null): string | void => {
      if (value === null || value === undefined) return;

      if (value < 0) return "Value cannot be negative";

      // Allow decimal values with up to 2 decimal places
      const decimalPlaces = value.toString().split('.')[1]?.length || 0;
      if (decimalPlaces > 2) return "Maximum 2 decimal places allowed";

      const max = getMainMaxValue();
      if (max > 0 && value > max) return `Value cannot exceed ${max}`;
    };

    const validateSubValue = (value: number | null): string | void => {
      if (value === null || value === undefined) return;

      if (value < 0) return "Value cannot be negative";
      if (value <= 0) return getErrorMessage(type);

      // Allow decimal values with up to 2 decimal places
      const decimalPlaces = value.toString().split('.')[1]?.length || 0;
      if (decimalPlaces > 2) return "Maximum 2 decimal places allowed";

      const max = getSubMaxValue();
      if (value > max) return `Value cannot exceed ${max}`;
    };

    return (
      <DimensionInputGroup>
        {/* Main Unit (only for weight) */}
        <Form.Item
          name={[type, "main"]}
          initialValue={0}
          rules={mainUnit ? [
            {
              validator: async (_, value) => {
                const mainValue = validateNumber(value) ?? 0;
                const subValue = validateNumber(form.getFieldValue([type, "sub"])) ?? 0;

                if (mainValue === 0 && subValue === 0) {
                  throw new Error(getErrorMessage(type));
                }

                const error = validateMainValue(mainValue);
                if (error) throw new Error(error);
              },
            },
          ] : []}
          className="dimension-form-item"
          hidden={!mainUnit}
        >
          <StyledNumberInput
            size="large"
            min={0}
            max={getMainMaxValue()}
            placeholder="00.00"
            onChange={(value) => handleValueChange(value, type, "main")}
            unit={mainUnit}
            allowDecimals={true}
            onFocus={(e) => {
              const value = form.getFieldValue([type, "main"]);
              if (value < 0.1) {
                form.setFieldsValue({
                  [type]: { main: "", sub: "" },
                });
              }
            }}
          />
        </Form.Item>

        {/* Sub Unit (for dimensions) */}
        <Form.Item
          name={[type, "sub"]}
          dependencies={[[type, "main"]]}
          initialValue={0}
          rules={subUnit ? [
            {
              validator: async (_, value) => {
                const subValue = validateNumber(value) ?? 0;
                const error = validateSubValue(subValue);
                if (error) throw new Error(error);
              },
            },
          ] : []}
          className="dimension-form-item"
          hidden={!subUnit}
        >
          <StyledNumberInput
            size="large"
            min={0}
            max={getSubMaxValue()}
            placeholder={unit === 1 ? "00.00" : "000.00"}
            onChange={(value) => handleValueChange(value, type, "sub")}
            onBlur={() => handleSubValueBlur(type)}
            unit={subUnit}
            allowDecimals={true}
            onFocus={(e) => {
              const value = form.getFieldValue([type, "sub"]);
              if (value < 0.1) {
                form.setFieldsValue({
                  [type]: { main: "", sub: "" },
                });
              }
            }}
          />
        </Form.Item>
      </DimensionInputGroup>
    );
  };

  return (
    <StepContainer>
      <Title>Package Dimensions</Title>
      <Form form={form} layout="vertical" initialValues={{ measurementType: 1 }}>
        <DimensionsContainer>
          <MeasurementUnitGroup>
            <UnitLabel>Measurement Unit</UnitLabel>
            <Space size={24}>
              <Form.Item className="measurement-unit-form-item" name="measurementType" rules={[{ required: true, message: "Please select a measurement unit" }]}>
                <Radio.Group
                  options={[
                    {
                      label: "inches/lb",
                      value: 1,
                    },
                    {
                      label: "cm/kg",
                      value: 2,
                    },
                  ]}
                  onChange={handleUnitChange}
                />
              </Form.Item>
              <Tooltip
                title={
                  <>
                    Note: Select <b>Imperial System (in/ lb)</b> or <b>Metric System (cm/ kg)</b>, then enter values in separate fields.
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Inches (in): 0 to 99.99</li>
                      <li>Pounds (lb): 0 to 99.99</li>
                      <li>Centimeters (cm): 0 to 999.99</li>
                      <li>Kilograms (kg): 0 to 99.99</li>
                    </ul>
                    All values can have up to 2 decimal places.
                  </>
                }
                placement="right"
                overlayStyle={{ maxWidth: 400 }}
              >
                <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer', marginLeft: 8 }} />
              </Tooltip>
            </Space>
          </MeasurementUnitGroup>

          <DimensionGrid>
            <DimensionGroup>
              <DimensionLabel required>Length</DimensionLabel>
              {renderDimensionInput("length")}
            </DimensionGroup>

            <DimensionGroup>
              <DimensionLabel required>Width</DimensionLabel>
              {renderDimensionInput("width")}
            </DimensionGroup>

            <DimensionGroup>
              <DimensionLabel required>Height</DimensionLabel>
              {renderDimensionInput("height")}
            </DimensionGroup>

            <DimensionGroup>
              <DimensionLabel required>Weight</DimensionLabel>
              {renderDimensionInput("weight")}
            </DimensionGroup>
          </DimensionGrid>

          <Form.Item name="description" label={<DimensionLabel isNoMarginBottom={true}>Contents</DimensionLabel>} rules={[{ required: true, message: "Please enter contents" }]}>
            <ContentTextArea placeholder="Contents" />
          </Form.Item>

          <WarningNote>
            Certain items may be restricted or prohibited for shipping, including hazardous materials, perishables, and oversized packages. Please check with the courier for specific guidelines before sending.
          </WarningNote>
        </DimensionsContainer>
      </Form>
    </StepContainer>
  );
};

export default Step3;
